import type {
  AcquisitionMetrics, ActivationMetrics, ActivationWindow, AnalyticsDataSource, BreakdownRow, FunnelStep, JourneyFunnel, QueryFilters, CostMetrics, DataEnvelope, DateRange,
  FeatureUsageMetrics, Metric, OverviewMetrics, PriceComparisonMetrics,
  ReceiptMetrics, ReliabilityMetrics, RetentionMetrics, Series,
} from '../types/analytics';
import { rangeDays } from '../utils/dateRange';

const delay = (signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
  const timer = window.setTimeout(resolve, 120);
  signal?.addEventListener('abort', () => {
    window.clearTimeout(timer);
    reject(new DOMException('Aborted', 'AbortError'));
  }, { once: true });
});

const metric = (value: number | null, unit: Metric['unit'] = 'count', kind: Metric['kind'] = 'mock'): Metric => ({
  value,
  availability: value === null ? 'not_collected' : 'available',
  kind,
  unit,
});

const noise = (day: number, offset = 0) => Math.sin(day * 1.71 + offset) * 0.08 + Math.cos(day * 0.43 + offset) * 0.05;

function dates(range: DateRange): string[] {
  const days = rangeDays(range);
  return Array.from({ length: days }, (_, index) => {
    const date = new Date(`${range.start}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + index);
    return date.toISOString().slice(0, 10);
  });
}

function series(range: DateRange, id: string, label: string, base: number, growth: number, offset = 0): Series {
  return {
    id,
    label,
    points: dates(range).map((date, index) => ({
      date,
      value: Math.max(0, Math.round((base + index * growth) * (1 + noise(index, offset)))),
    })),
  };
}

const sum = (input: Series) => input.points.reduce((total, point) => total + (point.value ?? 0), 0);
const average = (input: Series) => sum(input) / Math.max(1, input.points.length);

function envelope<T>(range: DateRange, data: T): DataEnvelope<T> {
  return {
    data,
    meta: { range, generatedAt: new Date().toISOString(), source: 'mock', completeness: 'complete' },
  };
}

function funnel(entries: Array<[string, string, number]>): FunnelStep[] {
  const start = entries[0]![2];
  return entries.map(([id, label, users], index) => {
    const previous = index === 0 ? null : entries[index - 1]![2];
    return {
      id, label, users,
      conversionFromPrevious: previous === null ? null : Number((users / previous * 100).toFixed(1)),
      conversionFromStart: Number((users / start * 100).toFixed(1)),
      dropOffFromPrevious: previous === null ? null : Number(((1 - users / previous) * 100).toFixed(1)),
    };
  });
}

const journeys: JourneyFunnel[] = [
  { id: 'shopping', name: 'Shopping list', steps: funnel([['shopping_1', 'Opened the list', 640], ['shopping_2', 'Added an item', 415], ['shopping_3', 'Checked an item', 188], ['shopping_4', 'Completed shopping', 91]]) },
  { id: 'receipt_qr', name: 'QR receipt scan', steps: funnel([['receipt_qr_1', 'Started a QR scan', 310], ['receipt_qr_2', 'Scan succeeded', 261]]) },
  { id: 'receipt_photo', name: 'Photo receipt scan', steps: funnel([['receipt_photo_1', 'Started a photo scan', 140], ['receipt_photo_2', 'Scan succeeded', 96]]) },
  { id: 'budget', name: 'Budget', steps: funnel([['budget_1', 'Opened Budget', 212], ['budget_2', 'Created a budget', 118], ['budget_3', 'Added an expense', 74]]) },
  { id: 'price_search', name: 'Price search', steps: funnel([['price_search_1', 'Opened Price comparison', 520], ['price_search_2', 'Started a search', 402], ['price_search_3', 'Search returned results', 351]]) },
  { id: 'pantry', name: 'Pantry', steps: funnel([['pantry_1', 'Opened Pantry', 730], ['pantry_2', 'Added an item', 305], ['pantry_3', 'Marked an item as used', 142]]) },
];

const breakdownSeed: Record<string, Array<[string, number, number, number]>> = {
  app_version: [['1.1.0', 118, 322, 9], ['1.0.0', 4, 17, 1]],
  device_model: [['SM-A155F', 21, 54, 2], ['SM-S911B', 17, 49, 1], ['2201116SG', 12, 36, 1], ['POCO F2 Pro', 9, 22, 0]],
  os_version: [['14', 52, 141, 3], ['13', 38, 112, 4], ['15', 21, 61, 2]],
  country: [['Serbia', 120, 331, 10]],
  city: [['Belgrade', 49, 131, 3], ['Novi Sad', 18, 47, 2], ['Nis', 11, 30, 1]],
  acquisition_source: [['google-play / organic', 41, 138, 3], ['(direct) / (none)', 33, 94, 4], ['(not attributed) / -', 48, 107, 3]],
  install_source: [['com.android.vending', 112, 310, 8], ['manual_install', 6, 19, 1], ['com.google.android.packageinstaller', 4, 10, 1]],
  account_type: [['guest', 74, 201, 6], ['registered', 41, 118, 3], ['unknown', 7, 20, 1]],
};

const breakdownLabels = {
  app_version: 'App version', device_model: 'Device model', os_version: 'Android version', country: 'Country', city: 'City',
  acquisition_source: 'Acquisition source', install_source: 'Install channel', account_type: 'Account type',
};

function breakdowns(): Record<string, BreakdownRow[]> {
  return Object.fromEntries(Object.entries(breakdownSeed).map(([dimension, rows]) => {
    const total = rows.reduce((sumOfRows, row) => sumOfRows + row[1], 0);
    return [dimension, rows.map(([key, newInstalls, activeDevices, uninstalls]) => ({
      key, label: dimension === 'install_source' ? ({ 'com.android.vending': 'Google Play', manual_install: 'Manual install (adb / flutter run)' } as Record<string, string>)[key] ?? key : key,
      newInstalls, activeDevices, uninstalls, share: Number((newInstalls / total * 100).toFixed(1)),
    }))];
  }));
}

export class MockAnalyticsDataSource implements AnalyticsDataSource {
  readonly kind = 'mock' as const;

  async getOverview(range: DateRange, signal?: AbortSignal) {
    await delay(signal);
    const active = series(range, 'dau', 'Daily active users', 286, 2.3, 1);
    const receipts = series(range, 'receipts', 'Receipts', 138, 1.35, 2);
    const costs = series(range, 'cost', 'Estimated cost', 21, 0.14, 4);
    costs.points = costs.points.map((point) => ({ ...point, value: Number(((point.value ?? 0) + 0.38).toFixed(2)) }));
    const dau = Math.round(average(active));
    const receiptTotal = sum(receipts);
    return envelope<OverviewMetrics>(range, {
      kpis: {
        registeredUsers: metric(8420), verifiedUsers: metric(7138), newRegistrations: metric(Math.round(rangeDays(range) * 12.6)), newUsers: metric(Math.round(rangeDays(range) * 31.4)),
        dau: metric(dau), wau: metric(1328), mau: metric(3942), receiptsScanned: metric(receiptTotal),
        receiptUsers: metric(Math.round(dau * 0.41)), receiptsPerReceiptUser: metric(Number((receiptTotal / Math.round(dau * 0.41)).toFixed(2)), 'ratio'),
        priceSearches: metric(Math.round(rangeDays(range) * 186.4)), priceComparisonUsers: metric(Math.round(dau * 0.34)),
        pantryUsers: metric(Math.round(dau * 0.61)), budgetUsers: metric(Math.round(dau * 0.27)),
        shoppingListUsers: metric(Math.round(dau * 0.48)), crashFreeUsers: metric(99.72, 'percent'),
        crashFreeSessions: metric(99.41, 'percent'), uninstalls: metric(Math.round(rangeDays(range) * 0.6) + 1), activeDevices: metric(Math.round(dau * 1.3)),
      },
      raw: { newUsers: metric(Math.round(rangeDays(range) * 31.4) + 11) },
      activeUsers: active,
      receiptUsage: receipts,
      featureAdoption: [
        { feature: 'Receipt', users: 1437 }, { feature: 'Pantry', users: 2134 },
        { feature: 'Budget', users: 948 }, { feature: 'Shopping', users: 1682 },
        { feature: 'Price comparison', users: 1196 },
      ],
      costTrend: costs,
    });
  }

  async getAcquisition(range: DateRange, signal?: AbortSignal, filters?: QueryFilters) {
    await delay(signal);
    const installs = series(range, 'installs', 'New installs', 9, 0.05, 1);
    const raw = { ...series(range, 'raw-installs', 'New installs (all devices)', 11, 0.06, 1) };
    raw.points = raw.points.map((point, index) => ({ ...point, value: Math.max(point.value ?? 0, installs.points[index]?.value ?? 0) }));
    const active = series(range, 'active', 'Active devices', 41, 0.4, 2);
    const uninstalls = series(range, 'uninstalls', 'Uninstall signals', 1, 0, 3);
    const total = sum(installs);
    const rawTotal = sum(raw);
    const uninstallTotal = sum(uninstalls);
    return envelope<AcquisitionMetrics>(range, {
      kpis: {
        newInstalls: metric(total), activeDevices: metric(Math.round(average(active) * 2.1)), uninstalls: metric(uninstallTotal), uninstallsFromNewInstalls: metric(Math.min(uninstallTotal, 2)), installsWithoutUninstall: metric(total - Math.min(uninstallTotal, 2)),
        activeFromNewInstalls: metric(Math.round(total * 0.8)), activeFromEarlierInstalls: metric(Math.max(0, Math.round(average(active) * 2.1) - Math.round(total * 0.8))),
        rawNewInstalls: metric(rawTotal), rawUninstalls: metric(uninstallTotal + 1), excludedInstalls: metric(rawTotal - total),
      },
      installsTrend: installs, rawInstallsTrend: raw, activeTrend: active, uninstallsTrend: uninstalls,
      breakdowns: breakdowns(), breakdownLabels,
      attribution: {
        status: 'partial',
        firstTouch: breakdowns().acquisition_source ?? [],
        notes: [
          'Source is the GA4 first-touch traffic source, filled from the Google Play install referrer. Devices without a delivered referrer show as not attributed.',
          'Mock note: session-level source reports can be skewed by the `source` event parameter; only user-level first touch is used here.',
          'Google Play Console acquisition data is not exported to BigQuery for this project, so store installs cannot be compared with first_open here.',
        ],
        googlePlayConsole: { connected: false, reason: 'No Play Console BigQuery export or Reporting API access is configured.' },
      },
      exclusions: {
        scope: filters?.scope ?? 'filtered', registry: 'not_configured', devices: 14, totalDevices: 137,
        rules: [
          { id: 'non_store_install', label: 'Installed outside Google Play (sideload or adb)', devices: 10 },
          { id: 'internal_stage', label: 'Build marked as internal testing', devices: 2 },
          { id: 'debug_mode', label: 'GA4 debug mode', devices: 2 },
          { id: 'registry', label: 'Listed in the test-device registry', devices: 0 },
        ],
      },
    });
  }

  async getActivationFunnel(range: DateRange, signal?: AbortSignal) {
    await delay(signal);
    const counts = [64, 58, 55];
    const labels = ['Started sign-up', 'Completed sign-up', 'Verified'];
    const ids = ['sign_up_started', 'sign_up_completed', 'verified'] as const;
    const window = (eligible: number, activated: number): ActivationWindow => ({ eligible, activated, rate: metric(Number((activated / eligible * 100).toFixed(1)), 'percent') });
    return envelope<ActivationMetrics>(range, {
      steps: counts.map((users, index) => ({
        id: ids[index], label: labels[index], users,
        conversionFromPrevious: index === 0 ? null : Number((users / counts[index - 1] * 100).toFixed(1)),
        conversionFromStart: Number((users / counts[0] * 100).toFixed(1)),
        dropOffFromPrevious: index === 0 ? null : Number(((1 - users / counts[index - 1]) * 100).toFixed(1)),
      })),
      registrationNotes: ['Mock data. Registration only: sign-up started, completed and verified.', 'Sign-up events exist in the data from 2026-10-12.'],
      instrumentation: [
        { event: 'first_open', firstSeen: '2026-09-20', devices: 33 }, { event: 'login', firstSeen: '2026-09-20', devices: 11 },
        { event: 'guest_started', firstSeen: '2026-10-12', devices: 7 }, { event: 'sign_up_completed', firstSeen: '2026-10-12', devices: 4 },
      ],
      installFunnel: {
        segments: [
          { segment: 'all', steps: funnel([['first_open', 'Installed and opened', 300], ['first_screen', 'Reached a first screen', 246], ['first_action', 'Took a first meaningful action', 131]]), within24h: window(240, 96), within7d: window(180, 99), returned: window(220, 88) },
          { segment: 'guest', steps: funnel([['first_open', 'Installed and opened', 190], ['first_screen', 'Reached a first screen', 150], ['first_action', 'Took a first meaningful action', 70]]), within24h: window(150, 48), within7d: window(110, 50), returned: window(140, 44) },
          { segment: 'registered', steps: funnel([['first_open', 'Installed and opened', 98], ['first_screen', 'Reached a first screen', 88], ['first_action', 'Took a first meaningful action', 58]]), within24h: window(80, 40), within7d: window(60, 45), returned: window(72, 38) },
          { segment: 'unknown', steps: funnel([['first_open', 'Installed and opened', 12], ['first_screen', 'Reached a first screen', 8], ['first_action', 'Took a first meaningful action', 3]]), within24h: window(10, 2), within7d: window(8, 3), returned: window(9, 2) },
        ],
        timeToFirstScreen: { p50: metric(14, 'seconds'), p90: metric(95, 'seconds') },
        timeToFirstAction: { p50: metric(310, 'seconds'), p90: metric(5400, 'seconds') },
        firstActions: [{ action: 'Added a shopping item', devices: 54, share: 41.2 }, { action: 'Scanned a receipt', devices: 37, share: 28.2 }, { action: 'Searched prices', devices: 26, share: 19.8 }, { action: 'Added a pantry item', devices: 14, share: 10.7 }],
        coreActivation: [
          { id: 'shopping_opened', label: 'Opened the shopping list', devices: 142, share: 47.3 }, { id: 'shopping_item_added', label: 'Added a shopping item', devices: 91, share: 30.3 },
          { id: 'receipt_succeeded', label: 'Scanned a receipt', devices: 59, share: 19.7 }, { id: 'price_search_started', label: 'Searched prices', devices: 63, share: 21 },
        ],
        notes: ['Mock data. The cohort is devices whose first_open fell in the selected range.', 'Time to first action is measured only among devices that reached the step.'],
      },
      journeys,
    });
  }

  async getRetention(range: DateRange, signal?: AbortSignal) {
    await delay(signal);
    const labels = ['D0', 'D1', 'D2', 'D3', 'D7', 'D14', 'D30'];
    const cohorts = Array.from({ length: Math.min(8, Math.max(4, Math.ceil(rangeDays(range) / 7))) }, (_, index) => {
      const date = new Date(`${range.end}T12:00:00Z`);
      date.setUTCDate(date.getUTCDate() - index * 7);
      const values = [100, 43 - index, 36 - index, 32 - index, 25 - index * 0.7, 19 - index * 0.5, 14 - index * 0.4];
      return {
        cohort: `Week of ${date.toISOString().slice(0, 10)}`,
        size: 224 + index * 19,
        periods: values.map((value, period) => period > index + 2 ? null : Number(Math.max(8, value).toFixed(1))),
      };
    });
    const withDetail = cohorts.map((cohort, index) => ({
      ...cohort,
      retained: cohort.periods.map((value) => (value === null ? null : Math.round(value / 100 * cohort.size))),
      uninstalledDevices: 3 + index, medianHoursToUninstall: 30 + index * 2, mature: cohort.periods.every((value) => value !== null),
    }));
    const frequency = [['1 day', 612], ['2 days', 203], ['3-4 days', 140], ['5-7 days', 77], ['8+ days', 38]] as const;
    const recency = [['0-1 days ago', 402], ['2-7 days ago', 288], ['8-14 days ago', 171], ['15+ days ago', 209]] as const;
    return envelope<RetentionMetrics>(range, {
      headline: { d1: metric(42.8, 'percent'), d7: metric(24.6, 'percent'), d30: metric(13.9, 'percent') },
      headlineBase: { d1: 1480, d7: 920, d30: 310 },
      cohorts: withDetail, periodLabels: labels,
      activity: {
        activeDevices: metric(1070), newDevices: metric(412), returningDevices: metric(658), mau: metric(1560), stickiness: metric(17.4, 'percent'),
        frequency: frequency.map(([bucket, devices]) => ({ bucket, devices })), recency: recency.map(([bucket, devices]) => ({ bucket, devices })),
        activeTrend: series(range, 'active', 'Active devices', 128, 0.8, 1), inactiveTrend: series(range, 'inactive', 'Inactive 7+ days (seen in the prior 30)', 61, 0.4, 2),
      },
      uninstalls: {
        devices: metric(68), installs: metric(1480), rate: metric(4.6, 'percent'),
        note: 'app_remove is a GA4 signal sent when Android reports the app removed. It is not confirmed churn: it can arrive late or be missed, and a reinstall starts a new device id, so the same person can appear as a new install.',
      },
    });
  }

  async getFeatureUsage(range: DateRange, signal?: AbortSignal) {
    await delay(signal);
    const definitions = [
      ['receipt', 'Receipt scan', 1437, 36.5, 4128, 618],
      ['pantry', 'Pantry', 2134, 54.1, 12842, 1540],
      ['budget', 'Budget', 948, 24.0, 3681, 493],
      ['shopping', 'Shopping List', 1682, 42.7, 9310, 1064],
      ['price', 'Price Comparison', 1196, 30.3, 5592, 704],
      ['checkin', 'Check-in', 734, 18.6, 1719, 316],
    ] as const;
    return envelope<FeatureUsageMetrics>(range, {
      features: definitions.map(([id, name, uniqueUsers, activeUserShare, eventCount, repeatUsers]) => ({
        id, name, uniqueUsers, activeUserShare, eventCount,
        eventsPerUser: Number((eventCount / uniqueUsers).toFixed(1)), repeatUsers,
        repeatRate: Number((repeatUsers / uniqueUsers * 100).toFixed(1)),
      })),
      trends: definitions.map(([id, name], index) => series(range, id, name, 35 + index * 10, 0.45, index)),
      activeDevices: metric(3942), journeys,
    });
  }

  async getReceiptMetrics(range: DateRange, signal?: AbortSignal) {
    await delay(signal);
    const receiptTrend = series(range, 'processed', 'Processed', 138, 1.35, 2);
    const processed = sum(receiptTrend);
    const failed = Math.round(processed * 0.043);
    const duplicates = Math.round(processed * 0.027);
    const successful = processed - failed - duplicates;
    return envelope<ReceiptMetrics>(range, {
      processed: metric(processed), successful: metric(successful), failed: metric(failed), duplicates: metric(duplicates),
      successRate: metric(Number((successful / processed * 100).toFixed(1)), 'percent'), uniqueUsers: metric(Math.round(processed / 3.1)),
      receiptsPerUser: metric(3.1, 'ratio'), avgItemsPerReceipt: metric(14.7, 'ratio'), avgProcessingDuration: metric(1840, 'milliseconds'),
      p50Latency: metric(1310, 'milliseconds'), p95Latency: metric(4120, 'milliseconds'), cacheHitRate: metric(37.4, 'percent'),
      enrichmentCalls: metric(Math.round(processed * 0.68)), receiptsTrend: receiptTrend,
      outcomeTrends: [
        { ...receiptTrend, id: 'success', label: 'Successful', points: receiptTrend.points.map((p) => ({ ...p, value: Math.round((p.value ?? 0) * .93) })) },
        { ...receiptTrend, id: 'failure', label: 'Failed', points: receiptTrend.points.map((p) => ({ ...p, value: Math.round((p.value ?? 0) * .043) })) },
      ],
      latencyTrend: [series(range, 'p50', 'p50', 1280, 0, 1), series(range, 'p95', 'p95', 4050, 0, 3)],
      retailers: [
        { retailer: 'Lidl', receipts: Math.round(processed * .27), share: 27 }, { retailer: 'Maxi', receipts: Math.round(processed * .23), share: 23 },
        { retailer: 'IDEA', receipts: Math.round(processed * .18), share: 18 }, { retailer: 'Univerexport', receipts: Math.round(processed * .13), share: 13 },
        { retailer: 'Other', receipts: Math.round(processed * .19), share: 19 },
      ],
    });
  }

  async getPriceComparisonMetrics(range: DateRange, signal?: AbortSignal) {
    await delay(signal);
    const searches = series(range, 'searches', 'Searches', 181, 1.25, 4);
    const total = sum(searches);
    const noResult = Math.round(total * .116);
    const failures = Math.round(total * .026);
    const success = total - noResult - failures;
    return envelope<PriceComparisonMetrics>(range, {
      totalSearches: metric(total), uniqueUsers: metric(Math.round(total / 4.7)), searchesPerUser: metric(4.7, 'ratio'),
      successfulSearches: metric(success), noResultSearches: metric(noResult), failures: metric(failures),
      noResultRate: metric(11.6, 'percent'), avgResultsReturned: metric(8.4, 'ratio'), avgRetailersPerResult: metric(3.2, 'ratio'),
      avgLatency: metric(690, 'milliseconds'), p50Latency: metric(510, 'milliseconds'), p95Latency: metric(1620, 'milliseconds'),
      browseRequests: metric(Math.round(total * .52)), browsePagesLoaded: metric(Math.round(total * .83)), avgProductsPerBrowse: metric(21.6, 'ratio'),
      loadMoreUsage: metric(38.2, 'percent'), searchesTrend: searches,
      outcomeTrends: [
        { ...searches, id: 'success', label: 'Successful', points: searches.points.map((p) => ({ ...p, value: Math.round((p.value ?? 0) * .858) })) },
        { ...searches, id: 'no-result', label: 'No result', points: searches.points.map((p) => ({ ...p, value: Math.round((p.value ?? 0) * .116) })) },
      ],
      latencyTrend: [series(range, 'p50', 'p50', 510, 0, 2), series(range, 'p95', 'p95', 1620, 0, 5)],
    });
  }

  async getCostMetrics(range: DateRange, signal?: AbortSignal) {
    await delay(signal);
    const daily = series(range, 'cost', 'Estimated daily cost', 21, .14, 4);
    daily.points = daily.points.map((point) => ({ ...point, value: Number(((point.value ?? 0) + .38).toFixed(2)) }));
    const total = Number(sum(daily).toFixed(2));
    return envelope<CostMetrics>(range, {
      totalCost: metric(total, 'currency', 'estimated'), averageCostPerDay: metric(Number((total / rangeDays(range)).toFixed(2)), 'currency', 'estimated'),
      aiCost: metric(Number((total * .39).toFixed(2)), 'currency', 'estimated'), costPerActiveUser: metric(.18, 'currency', 'estimated'),
      costPerReceipt: metric(.071, 'currency', 'estimated'), costPerPriceSearch: metric(.018, 'currency', 'estimated'),
      aiCallsPerReceipt: metric(.68, 'ratio', 'estimated'), dailyCost: daily,
      aiCalls: metric(Math.round(total * 11)), aiCostPerCall: metric(.0021, 'currency'), aiTrend: series(range, 'ai-cost', 'AI cost', 3, 0.02, 5),
      aiByModel: [{ sku: 'Gemini 2.5 flash lite input tokens', cost: Number((total * .12).toFixed(4)), usage: 812000, unit: 'token' }, { sku: 'Gemini 2.5 flash lite output tokens', cost: Number((total * .27).toFixed(4)), usage: 301000, unit: 'token' }],
      aiSource: 'Mock data. Cost: Cloud Billing export. Calls: backend analytics log.',
      byService: [
        { service: 'Cloud Functions', cost: total * .26, kind: 'estimated' }, { service: 'Firestore', cost: total * .19, kind: 'estimated' },
        { service: 'AI / enrichment', cost: total * .39, kind: 'estimated' }, { service: 'Cloud Run', cost: total * .1, kind: 'estimated' },
        { service: 'Other', cost: total * .06, kind: 'estimated' },
      ], currency: 'USD',
    });
  }

  async getSystemHealth(range: DateRange, signal?: AbortSignal) {
    await delay(signal);
    return envelope<ReliabilityMetrics>(range, {
      crashFreeUsers: metric(99.72, 'percent'), crashCount: metric(11), nonFatalErrors: metric(84),
      receiptFailureRate: metric(4.3, 'percent'), priceSearchFailureRate: metric(2.6, 'percent'),
      receiptLatency: metric(1840, 'milliseconds'), receiptP95Latency: metric(4120, 'milliseconds'),
      priceSearchLatency: metric(690, 'milliseconds'), priceSearchP95Latency: metric(1620, 'milliseconds'),
      errorsTrend: [series(range, 'crashes', 'Crashes', 1, 0, 2), series(range, 'nonfatal', 'Non-fatal', 4, .05, 4)],
      latencyTrend: [series(range, 'receipt', 'Receipt p95', 4050, -2, 1), series(range, 'price', 'Price search p95', 1640, -1, 3)],
      crashFreeSessions: metric(99.41, 'percent'), sessions: metric(2840), crashedSessions: metric(17), crashedUsers: metric(9),
      startup: { p50: metric(570, 'milliseconds'), p95: metric(1480, 'milliseconds'), count: metric(412), byVersion: [{ version: '1.1.0', count: 398, p50Ms: 565, p95Ms: 1460 }] },
      crashesByVersion: [{ version: '1.1.0', build: '14', crashes: 9, crashedSessions: 8, sessions: 2410 }, { version: '1.1.0', build: '13', crashes: 2, crashedSessions: 2, sessions: 430 }],
      topIssues: [{ issueId: 'a1', title: 'BudgetService._userId', type: 'FATAL', crashes: 5, sessions: 4 }, { issueId: 'b2', title: 'ShoppingListScreen._showAddDialog', type: 'FATAL', crashes: 3, sessions: 3 }],
      performanceTraces: [
        { trace: 'receipt_import', count: metric(88), p50Latency: metric(21000, 'milliseconds'), p95Latency: metric(48000, 'milliseconds') },
        { trace: 'smart_hub_load', count: metric(402), p50Latency: metric(480, 'milliseconds'), p95Latency: metric(1100, 'milliseconds') },
      ],
      exports: [
        { id: 'crashlytics', status: 'ready', reason: null, earliestDate: '2026-10-12' }, { id: 'performance', status: 'ready', reason: null, earliestDate: '2026-09-20' },
        { id: 'sessions', status: 'ready', reason: null, earliestDate: '2026-09-20' },
      ],
    });
  }
}
